import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StarIcon, User, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { getProductReviews, submitReview } from "@/utils/helper/product";
import { toast } from "sonner";

export default function ProductReviews({ productId }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviews = await getProductReviews(productId);
        setReviews(reviews);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [productId]);

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!reviewText || rating === 0) {
      toast.error("Please provide a rating and review.");
      return;
    }

    setIsSubmitting(true);
    try {
      const newReview = await submitReview(productId, {
        rating,
        review: reviewText,
      });
      setReviews([newReview, ...reviews]);
      setRating(0);
      setReviewText("");
      setShowForm(false);
    } catch (error) {
      toast.error(error.message); // ✅ shows backend error
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (currentRating, interactive = false) => {
    return [...Array(5)].map((_, i) => {
      const starValue = i + 1;
      return (
        <StarIcon
          key={i}
          className={cn(
            "w-5 h-5 cursor-pointer transition-colors",
            interactive && "hover:scale-110",
            starValue <= (interactive ? hoverRating || rating : currentRating)
              ? "fill-yellow-400 text-yellow-400"
              : "text-gray-300"
          )}
          onClick={interactive ? () => handleStarClick(starValue) : undefined}
          onMouseEnter={
            interactive ? () => setHoverRating(starValue) : undefined
          }
          onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
        />
      );
    });
  };

  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, review) => sum + review.rating, 0) /
          reviews.length
        ).toFixed(1)
      : 0;

  return (
    <div className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Reviews Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Customer Reviews
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                {renderStars(Math.round(averageRating))}
              </div>
              <span className="text-lg font-semibold">{averageRating}</span>
              <span className="text-gray-600">({reviews.length} reviews)</span>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="mb-6"
              variant={showForm ? "outline" : "default"}
            >
              <Plus className="w-4 h-4 mr-2" />
              {showForm ? "Cancel Review" : "Write a Review"}
            </Button>
          </div>

          {showForm && (
            <Card className="mb-8 max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Write a Review</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <Label>Rating *</Label>
                    <div className="flex items-center gap-1 mt-1">
                      {renderStars(rating, true)}
                      <span className="ml-2 text-sm text-gray-600">
                        {rating > 0
                          ? `${rating} star${rating > 1 ? "s" : ""}`
                          : "Select a rating"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reviewText">Review *</Label>
                    <Textarea
                      id="reviewText"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience with this product..."
                      rows={4}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Review"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Reviews list */}
          {loading ? (
            <p className="text-center text-gray-500">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p className="text-center text-gray-500">
              No reviews yet. Be the first to write one!
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <Card key={index} className="h-fit">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-800 truncate">
                          {review.user_name || "Anonymous"}
                        </h4>
                        <p className="text-sm text-gray-500 truncate">
                          {review.user_email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-0.5">
                        {renderStars(review.rating)}
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(
                          review.created_at || review.date
                        ).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      {review.review}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
